import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find(v => v.id === id);

  if (!video) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
          <p className="mt-4 text-foreground-600">视频不存在或已被删除</p>
          <Link to="/" className="inline-block mt-4 px-6 py-2 text-sm font-medium whitespace-nowrap cursor-pointer rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors">返回首页</Link>
        </div>
      </main>
    );
  }

  const relatedVideos = videos.filter(v => v.categorySlug === video.categorySlug && v.id !== video.id).slice(0, 3);

  return (
    <main>
      <Breadcrumb items={[
        { label: '分类浏览', href: '/category/dance' },
        { label: video.category, href: `/category/${video.categorySlug}` },
        { label: video.title },
      ]} />

      <article className="w-full py-6 md:py-10 bg-background-50">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Link to={`/category/${video.categorySlug}`} className="px-3 py-1 text-xs rounded-full bg-accent-100 text-accent-800 hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer">
                {video.category}
              </Link>
              {video.tags.map(tag => (
                <span key={tag} className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-500 whitespace-nowrap">#{tag}</span>
              ))}
            </div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-snug mb-4 font-heading">{video.title}</h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <div className="flex items-center gap-2">
                <img src={video.authorAvatar} alt={video.author} className="w-8 h-8 rounded-full object-cover" />
                <span className="font-medium text-foreground-800">{video.author}</span>
              </div>
              <span>|</span>
              <span>{video.date}</span>
              <span>|</span>
              <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{video.views >= 10000 ? `${(video.views / 10000).toFixed(0)}万` : video.views} 次观看</span>
              <span className="flex items-center gap-1"><i className="ri-heart-line"></i>{video.likes >= 10000 ? `${(video.likes / 10000).toFixed(0)}万` : video.likes} 点赞</span>
            </div>
          </header>

          <div className="relative aspect-video rounded-xl overflow-hidden mb-8">
            <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-foreground-950/30 flex items-center justify-center cursor-pointer group">
              <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 group-hover:bg-primary-500 transition-colors group-hover:scale-110 transition-transform">
                <i className="ri-play-fill text-3xl md:text-4xl"></i>
              </div>
            </div>
          </div>

          <div className="prose max-w-none text-foreground-700 leading-relaxed text-sm md:text-base [&_p]:mb-4 [&_p]:leading-relaxed" dangerouslySetInnerHTML={{ __html: video.content }} />

          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <button className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium whitespace-nowrap cursor-pointer rounded-full border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-500 transition-colors">
                  <i className="ri-heart-line"></i> 点赞
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium whitespace-nowrap cursor-pointer rounded-full border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-500 transition-colors">
                  <i className="ri-bookmark-line"></i> 收藏
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium whitespace-nowrap cursor-pointer rounded-full border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-500 transition-colors">
                  <i className="ri-share-forward-line"></i> 分享
                </button>
              </div>
              <Link to={`/category/${video.categorySlug}`} className="text-sm text-primary-500 hover:text-primary-600 transition-colors font-medium whitespace-nowrap cursor-pointer">
                查看更多{video.category}视频 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </article>

      {relatedVideos.length > 0 && (
        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl font-bold text-foreground-950 mb-6 font-heading">相关推荐</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedVideos.map(rv => (
                <Link
                  key={rv.id}
                  to={`/article/${rv.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-background-300/60 transition-all cursor-pointer"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img src={rv.thumbnail} alt={rv.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute bottom-2 right-2 bg-foreground-950/75 text-background-50 text-xs px-2 py-0.5 rounded">{rv.duration}</div>
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 mb-2 leading-snug">{rv.title}</h3>
                    <div className="flex items-center gap-2 text-xs text-foreground-400">
                      <span>{rv.author}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{(rv.views / 10000).toFixed(0)}万</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        'headline': video.title,
        'description': video.description,
        'datePublished': video.date,
        'author': { '@type': 'Person', 'name': video.author },
        'image': video.thumbnail,
        'publisher': { '@type': 'Organization', 'name': '91抖音', 'url': 'https://91douyin.com/' },
      }) }} />
    </main>
  );
}